import {
  CYCLICALITY,
  SIZE_SEGMENTS,
  VISIBLE_SCOPES,
} from '../../config/constants/pointInTimeData';
import { UniverseSelector } from '../../components/Universe/UniverseSelector';
import { UniverseDetails } from '../../components/Universe/UniverseDetails';
import { UniverseStatsRow } from '../../components/Universe/UniverseStatsRow';
import { MultiSelectCardGroup } from '../../components/Universe/MultiSelectCardGroup';
import type { Universe } from '../../types';
import { Card } from '@/components/ui/card';
import type { DataStatus } from '@/features/Strategies/features/SmartStrategyDefinitonForm/hooks/dataStatus';

interface PointInTimeProps {
  universeId: string;
  geographicScope: Array<string>;
  sizeSegment: Array<string>;
  cyclicality: Array<string>;
  selectedUniverse: Universe;
  /** Availability of the CH-measured numbers (countries/constituents/mcap). */
  metricsStatus?: DataStatus;
  disabledScopes?: Set<string>;
  stats: {
    countries: number;
    countriesInitial: number;
    constituents: number;
    universeSize: number;
    coverage: number;
    coverageInitial: number;
    marketCap: number;
    marketCapInitial: number;
  };
  disabledSizeSegments?: Set<string>;
  disabledCyclicality?: Set<string>;
  onUniverseSelect: (universeId: string) => void;
  onGeographicToggle: (scopeId: string) => void;
  onSizeToggle: (segmentId: string) => void;
  onCycleToggle: (cycleId: string) => void;
  /** Live "N excluded" estimates per selector (same badge as the
   *  sectors/groups/industries headers; hidden at 0). */
  scopeExcludedCount?: number;
  sizeExcludedCount?: number;
  cyclicalityExcludedCount?: number;
}

export function Universe({
  geographicScope,
  sizeSegment,
  cyclicality,
  selectedUniverse,
  disabledScopes,
  stats,
  metricsStatus,
  disabledSizeSegments,
  disabledCyclicality,
  onUniverseSelect,
  onGeographicToggle,
  onSizeToggle,
  onCycleToggle,
  scopeExcludedCount,
  sizeExcludedCount,
  cyclicalityExcludedCount,
}: PointInTimeProps) {
  const multiSelectHint = '(Multi-select - At least one required)';

  const disabledScopeIds = disabledScopes ? [...disabledScopes] : [];

  return (
    // Outer Card: matches Figma's "Point-in-time Universe" sub-card
    // (`border-primary/20 bg-primary/[0.02]`, no outer padding; `p-3`
    // applied to the inner wrapper so the section can grow without
    // shifting the badge row).
    <Card className="border-primary/20 bg-primary/[0.02]">
      <div className="p-3">
        {/* Header row: title + Morningstar provider badge. */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="font-medium">Point-in-time Universe</div>
            <div className="px-1.5 py-0.5 bg-primary/15 border border-primary/40 rounded text-xs">
              <span className="text-primary font-medium">Morningstar</span>
            </div>
          </div>
        </div>

        {/* The index dropdown + details are static (they don't need the
            catalog). Only the stats row's NUMBERS come from ClickHouse, so it
            skeletons just those when the catalog is unavailable. */}
        <UniverseSelector
          selectedUniverse={selectedUniverse}
          onSelect={onUniverseSelect}
        />

        <UniverseStatsRow
          region={selectedUniverse.coverageLabel || selectedUniverse.scope}
          stats={stats}
          metricsStatus={metricsStatus}
        />

        <UniverseDetails selectedUniverse={selectedUniverse} />

        {/* Figma divides each multi-select group with a top hairline
            (mt-3 pt-3 border-t border-border/20). */}
        <div className="mt-3 pt-3 border-t border-border/20">
          <MultiSelectCardGroup
            label="Geographic Scope"
            hint={multiSelectHint}
            items={VISIBLE_SCOPES}
            selectedIds={geographicScope}
            disabledIds={disabledScopeIds}
            onToggle={onGeographicToggle}
            minSelected={1}
            excludedCount={scopeExcludedCount}
          />
        </div>

        <div className="mt-3 pt-3 border-t border-border/20">
          <MultiSelectCardGroup
            label="Size Segment"
            hint={multiSelectHint}
            items={SIZE_SEGMENTS}
            selectedIds={sizeSegment}
            disabledIds={disabledSizeSegments ? [...disabledSizeSegments] : []}
            onToggle={onSizeToggle}
            minSelected={1}
            excludedCount={sizeExcludedCount}
          />
        </div>

        <div className="mt-3 pt-3 border-t border-border/20">
          <MultiSelectCardGroup
            label="Cyclicality"
            hint={multiSelectHint}
            items={CYCLICALITY}
            selectedIds={cyclicality}
            disabledIds={disabledCyclicality ? [...disabledCyclicality] : []}
            onToggle={onCycleToggle}
            minSelected={1}
            excludedCount={cyclicalityExcludedCount}
          />
        </div>
      </div>
    </Card>
  );
}
